import React, { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import LoginPage from './pages/Login/LoginPage';
import ChatPage from './pages/Chat/ChatPage';
import ProtectedRoute from './router/ProtectedRoute';
import { useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './theme/theme-provider';
import AdminRoute from './router/AdminRoute';
import AdminLayout from './components/AdminLayout';
import UserListPage from './pages/Admin/Users/UserListPage';

// Lazy load page-level components
const LoginPageLazy = lazy(() => import('./pages/Login/LoginPage'));
const ChatPageLazy = lazy(() => import('./pages/Chat/ChatPage'));
const AdminLayoutLazy = lazy(() => import('./components/AdminLayout'));
const UserListPageLazy = lazy(() => import('./pages/Admin/Users/UserListPage'));

// Loading fallback component
const LoadingFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <CircularProgress />
  </Box>
);

// AppContent component
const AppContent: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { theme, setTheme } = useTheme();

  return (
      <Router>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Public route */}
            <Route path="/login" element={<LoginPageLazy />} />

            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              <Route
                path="/chat"
              element={<ChatPageLazy />}
              />
            </Route>

            {/* Admin routes */}
            <Route path="/admin" element={<AdminRoute />}>
              <Route element={<AdminLayoutLazy />}>
                <Route path="users" element={<UserListPageLazy />} />
              <Route path="" element={<Navigate to="users" replace />} />
              </Route>
            </Route>

            {/* Redirect logic */}
            <Route 
              path="*" 
              element={<Navigate to={isAuthenticated ? "/chat" : "/login"} replace />} 
            />
          </Routes>
        </Suspense>
      </Router>
  );
}

// Main App component wraps everything in the new ThemeProvider and AuthProvider
const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
};

export default App; 